import { ControlPlaneClient, HandleLakehouseOauthDocument } from '@vendia/control-plane-api'
import { LakehouseConnectionTypeEnum, LakehouseSnowflakeConnectionInput } from '@vendia/management-api-types'
import { VendiaFormApi } from 'src/components/fields/form'

import notify from '../notify'

interface SnowflakeOAuthProps {
  snowflake: LakehouseSnowflakeConnectionInput
  form: VendiaFormApi
  uniName: string
  nodeName: string
  prefix: string
  api: ControlPlaneClient
  onSuccess: () => void
}

export const getOrigin = (): string => {
  let origin = import.meta.env.NEXT_PUBLIC_URL

  if (import.meta.env.NEXT_PUBLIC_VENDIA_ENV === 'dev') {
    origin = 'http://localhost:3000'
  }

  return origin
}

export const buildSnowflakeAuthorizeUrl = (account: string, clientId: string, role?: string): string => {
  const origin = getOrigin()

  let scope = 'refresh_token'

  if (role) {
    scope += ` session:role:${role}`
  }

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: clientId || '',
    redirect_uri: `${origin}/oauth/callback`,
    scope: `refresh_token session:role:${role}`,
  })

  return `https://${account}.snowflakecomputing.com/oauth/authorize?${params.toString()}`
}

export const setupOAuthMessageListener = ({
  snowflake,
  form,
  uniName,
  nodeName,
  prefix,
  api,
  onSuccess,
}: SnowflakeOAuthProps) => {
  const origin = getOrigin()

  const handleMessage = async (event: MessageEvent) => {
    if (event.origin !== origin) return

    if (event.data && event.data.type === 'oauth_success') {
      const code = event.data.code

      const response = await api.graphqlFetch({
        query: HandleLakehouseOauthDocument,
        variables: {
          uniName,
          nodeName,
          connectionInput: {
            type: LakehouseConnectionTypeEnum.Snowflake,
            snowflakeConnection: {
              ...(snowflake as LakehouseSnowflakeConnectionInput),
              oauth: { ...snowflake.oauth, code },
            },
          },
        },
      })

      if (response.errors) {
        notify.error(`${response.errors[0].message}`)
        return
      }

      form.setFieldValue(`${prefix}` as any, {
        ...snowflake?.oauth,
        accessToken: response.handleLakehouseOauth?.accessToken as string,
        accessTokenExpiry: response.handleLakehouseOauth?.accessTokenExpiry as string,
        refreshToken: response.handleLakehouseOauth?.refreshToken as string,
        refreshTokenExpiry: response.handleLakehouseOauth?.refreshTokenExpiry as string,
      })

      onSuccess()
    }
  }

  window.addEventListener('message', handleMessage)
  return () => window.removeEventListener('message', handleMessage)
}

export const getSnowflakeOAuthInfo = ({
  snowflake,
  uniName,
  nodeName,
  prefix,
  api,
  form,
  onSuccess,
}: SnowflakeOAuthProps) => {
  const authorizeUrl = buildSnowflakeAuthorizeUrl(
    snowflake?.account!,
    snowflake?.oauth?.clientId!,
    snowflake?.role ?? undefined,
  )

  return {
    authorizeUrl,
    setupListener: () =>
      setupOAuthMessageListener({
        snowflake,
        form,
        uniName,
        nodeName,
        prefix,
        api,
        onSuccess,
      }),
  }
}

export const filterS3IngestionFiles = (objects: Array<{ key: string }>): string[] => {
  return objects
    .filter((obj) => {
      const key = obj.key.toLowerCase()
      const hasNoExtension = !key.includes('.')
      const isCsv = key.endsWith('.csv')
      const isTxt = key.endsWith('.txt')
      return hasNoExtension || isCsv || isTxt
    })
    .map((obj) => obj.key)
}
